import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { User, CreditCard, Megaphone, Receipt, BadgeCheck, ArrowRight, MapPin, EyeOff, Crown, Heart, Eye, Phone } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CATEGORIES, CATEGORIES_ABONNEMENT } from "@/lib/categories";
import { getUser, formatFCFA, formatDate } from "@/lib/user";
import { MesTelephones } from "@/components/MesTelephones";
import { NumerosVus } from "@/components/NumerosVus";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PALIERS = { gratuit: "Gratuit", semaine: "Semaine", mois: "Mois" };

const Section = ({ icon: Icon, title, children, testid }) => (
  <motion.section
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45 }}
    data-testid={testid}
    className="bg-white border border-[#E5DFD5] rounded-3xl p-6 sm:p-8"
  >
    <h2 className="flex items-center gap-2.5 font-heading text-lg font-extrabold text-stone-900 mb-5">
      <Icon size={18} className="text-[#E05638]" /> {title}
    </h2>
    {children}
  </motion.section>
);

export default function TableauDeBord() {
  const user = getUser();
  const [profil, setProfil] = useState(null);
  const [acces, setAcces] = useState(null);
  const [annonces, setAnnonces] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [paiementsAbo, setPaiementsAbo] = useState([]);
  const [blindSaving, setBlindSaving] = useState(false);
  const [vipData, setVipData] = useState(null);

  const chargerVip = () =>
    axios.get(`${API}/profiles/${user.id}/club-vip`).then((r) => setVipData(r.data)).catch(() => {});

  const likerEnRetour = async (pid) => {
    try {
      await axios.post(`${API}/profiles/${pid}/like`, { visitor_id: user.id });
      toast.success("Like envoyé en retour !");
      chargerVip();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const toggleBlindDate = async () => {
    if (!profil || blindSaving) return;
    setBlindSaving(true);
    try {
      const res = await axios.post(`${API}/profiles/${user.id}/blind-date`, { enabled: !profil.blind_date });
      setProfil({ ...profil, blind_date: res.data.blind_date });
      toast.success(
        res.data.blind_date ? "Mode Blind Date activé" : "Mode Blind Date désactivé",
        { description: res.data.blind_date ? "Votre photo sera floutée et se révélera après quelques échanges." : "Votre photo est de nouveau visible normalement." }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBlindSaving(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/profiles/${user.id}`).then((r) => setProfil(r.data)).catch(() => {});
    axios.get(`${API}/access/${user.id}`).then((r) => setAcces(r.data)).catch(() => {});
    axios.get(`${API}/annonces`, { params: { profile_id: user.id } }).then((r) => setAnnonces(r.data)).catch(() => {});
    axios.get(`${API}/paiements`, { params: { profile_id: user.id } }).then((r) => setPaiements(r.data)).catch(() => {});
    axios.get(`${API}/paiements/abonnements`, { params: { profile_id: user.id } }).then((r) => setPaiementsAbo(r.data)).catch(() => {});
    chargerVip();
  }, []);

  if (!user) return <Navigate to="/connexion" replace />;
  const cat = CATEGORIES[profil?.category] || CATEGORIES.lago;

  // Fusion des paiements annonces + abonnements, triés du plus récent au plus ancien
  const historiquePaiements = [
    ...paiements.map((h) => ({ date: h.date, libelle: h.libelle, montant: h.montant, statut: h.statut })),
    ...paiementsAbo.map((h) => ({
      date: h.created_at,
      libelle: `Abonnement ${(CATEGORIES_ABONNEMENT[h.category]?.name || h.category)} · ${h.period === "week" ? "semaine" : "mois"}`,
      montant: h.montant, statut: h.statut,
    })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="bg-[#FAF8F5] min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 pt-32 pb-24 space-y-6" data-testid="dashboard-page">
        <div>
          <span className="text-[#E05638] text-sm font-semibold tracking-[0.25em] uppercase">Mon compte</span>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-stone-900 mt-2">
            Bonjour, {user.firstname}
          </h1>
        </div>

        <Section icon={User} title="Mon profil" testid="dashboard-profil">
          {profil ? (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-stone-700">
              <span className="font-heading font-bold text-stone-900 text-base">{profil.firstname}{profil.age ? `, ${profil.age} ans` : ""}</span>
              <span className="flex items-center gap-1"><MapPin size={13} /> {profil.city}{profil.quartier ? ` · ${profil.quartier}` : ""}</span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: cat.color }}>{cat.name}</span>
              {profil.telephone_verifie && profil.contact && (
                <span className="flex items-center gap-1 text-emerald-700 text-xs font-semibold"><BadgeCheck size={14} /> Téléphone vérifié</span>
              )}
              {profil.contact && <span className="text-stone-500">{profil.contact}</span>}
            </div>
          ) : (
            <p className="text-sm text-stone-500">Chargement…</p>
          )}
        </Section>

        <Section icon={Phone} title="Mes numéros de contact" testid="dashboard-telephones">
          <MesTelephones profileId={user.id} />
        </Section>

        <Section icon={Eye} title="Qui a regardé mon numéro" testid="dashboard-numeros-vus">
          <NumerosVus profileId={user.id} />
        </Section>

        {vipData && (
          <Section icon={Crown} title="Espace ClubVIP" testid="dashboard-club-vip">
            {vipData.vip ? (
              <div className="grid sm:grid-cols-2 gap-6">
                <div data-testid="vip-ils-mont-like">
                  <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">
                    <Heart size={13} className="text-[#E05638]" /> Ils m'ont liké
                  </h4>
                  {vipData.ils_m_ont_like.length === 0 ? (
                    <p className="text-xs text-stone-400">Personne pour le moment — restez visible avec une annonce !</p>
                  ) : (
                    <ul className="space-y-2">
                      {vipData.ils_m_ont_like.map((p) => (
                        <li key={p.id} className="flex items-center gap-3 bg-[#FAF8F5] rounded-xl px-3 py-2" data-testid={`vip-like-${p.id}`}>
                          <span className="w-8 h-8 shrink-0 rounded-full bg-[#B8860B] text-white text-sm font-bold flex items-center justify-center">{p.firstname.charAt(0)}</span>
                          <span className="text-sm text-stone-800 font-semibold truncate">
                            {p.firstname}{p.age ? `, ${p.age}` : ""} <span className="font-normal text-stone-400 text-xs">· {p.city}</span>
                          </span>
                          <button
                            onClick={() => likerEnRetour(p.id)}
                            data-testid={`vip-like-retour-${p.id}`}
                            className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#E05638] hover:underline"
                          >
                            <Heart size={12} /> Liker en retour
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div data-testid="vip-vu-recemment">
                  <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">
                    <Eye size={13} className="text-[#0A5C36]" /> Vu récemment (7 jours)
                  </h4>
                  {vipData.vus_recemment.length === 0 ? (
                    <p className="text-xs text-stone-400">Aucune visite ces 7 derniers jours.</p>
                  ) : (
                    <ul className="space-y-2">
                      {vipData.vus_recemment.map((p) => (
                        <li key={p.id} className="flex items-center gap-3 bg-[#FAF8F5] rounded-xl px-3 py-2" data-testid={`vip-vue-${p.id}`}>
                          <span className="w-8 h-8 shrink-0 rounded-full bg-[#0A5C36] text-white text-sm font-bold flex items-center justify-center">{p.firstname.charAt(0)}</span>
                          <span className="text-sm text-stone-800 font-semibold truncate">
                            {p.firstname}{p.age ? `, ${p.age}` : ""} <span className="font-normal text-stone-400 text-xs">· {p.city}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <Link
                  to="/contacts"
                  data-testid="vip-vers-contacts"
                  className="sm:col-span-2 mt-1 inline-flex items-center justify-between gap-3 rounded-2xl bg-[#12100E] px-5 py-4 text-white transition-transform hover:scale-[1.01]"
                >
                  <span className="flex items-center gap-3">
                    <Phone size={18} className="text-[#D4AF37]" />
                    <span className="text-sm font-semibold">Annuaire premium — voir les numéros des membres</span>
                  </span>
                  <ArrowRight size={16} className="text-[#D4AF37]" />
                </Link>
              </div>
            ) : (
              <div className="relative" data-testid="vip-apercu-floute">
                <div className="grid sm:grid-cols-2 gap-6 blur-sm select-none pointer-events-none" aria-hidden="true">
                  {[
                    { titre: "Ils m'ont liké", couleur: "#B8860B" },
                    { titre: "Vu récemment (7 jours)", couleur: "#0A5C36" },
                  ].map((col) => (
                    <div key={col.titre}>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-3">{col.titre}</h4>
                      <ul className="space-y-2">
                        {["Aya", "Koffi", "Fatou"].map((n) => (
                          <li key={n} className="flex items-center gap-3 bg-[#FAF8F5] rounded-xl px-3 py-2">
                            <span className="w-8 h-8 rounded-full text-white text-sm font-bold flex items-center justify-center" style={{ backgroundColor: col.couleur }}>{n.charAt(0)}</span>
                            <span className="text-sm text-stone-800 font-semibold">{n}, 2• · Abidjan</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/40 rounded-xl">
                  <p className="text-sm text-stone-700 font-semibold text-center px-4" data-testid="vip-teaser-compteurs">
                    {vipData.likes_count} membre(s) vous ont liké · {vipData.vues_count} visite(s) ces 7 derniers jours
                  </p>
                  <Link
                    to="/abonnement?cat=vip"
                    data-testid="vip-passer-btn"
                    className="inline-flex items-center gap-2 bg-[#B8860B] hover:opacity-90 text-white text-sm font-semibold px-6 py-2.5 rounded-full transition-opacity"
                  >
                    <Crown size={14} /> Passer VIP
                  </Link>
                </div>
              </div>
            )}
          </Section>
        )}

        {profil?.category === "love" && (
          <Section icon={EyeOff} title="Mode Blind Date" testid="dashboard-blind-date">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-stone-600 leading-relaxed max-w-md">
                Votre photo est <b>floutée</b> pour les autres membres et se <b>révèle progressivement</b> après 10 messages
                échangés avec la même personne — ou immédiatement si vous acceptez tous les deux de la dévoiler.
              </p>
              <button
                onClick={toggleBlindDate}
                disabled={blindSaving}
                data-testid="blind-date-toggle"
                role="switch"
                aria-checked={!!profil.blind_date}
                className={`relative w-14 h-8 rounded-full transition-colors duration-300 shrink-0 ${profil.blind_date ? "bg-[#9f2558]" : "bg-stone-300"}`}
              >
                <span
                  className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-all duration-300 ${profil.blind_date ? "left-7" : "left-1"}`}
                />
              </button>
            </div>
            <p className={`mt-3 text-xs font-semibold ${profil.blind_date ? "text-[#9f2558]" : "text-stone-400"}`} data-testid="blind-date-status">
              {profil.blind_date ? "Activé — votre photo est actuellement floutée pour les autres membres." : "Désactivé — votre photo est visible normalement."}
            </p>
          </Section>
        )}

        <Section icon={CreditCard} title="Mon abonnement" testid="dashboard-abonnement">
          {acces?.base ? (
            <div className="space-y-2 text-sm text-stone-700">
              <p>
                <b>{CATEGORIES_ABONNEMENT[acces.base.category]?.name}</b> ({acces.base.period === "week" ? "semaine" : "mois"}) —{" "}
                {formatFCFA(acces.base.price)} · expire le <b>{formatDate(acces.base.expires_at)}</b>
              </p>
              {acces.addons?.map((a) => (
                <p key={a.id}>+ Supplément <b>{CATEGORIES_ABONNEMENT[a.category]?.name}</b> · expire le {formatDate(a.expires_at)}</p>
              ))}
              {acces.vip_monthly_loyalty && <p className="text-[#0A5C36] font-semibold">Fidélité : accès FodindinLove offert</p>}
            </div>
          ) : (
            <p className="text-sm text-stone-500">Aucun abonnement actif.</p>
          )}
          <Link to="/abonnement" data-testid="dashboard-vers-abonnement" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#E05638] hover:underline">
            Gérer mon abonnement <ArrowRight size={14} />
          </Link>
        </Section>

        <Section icon={Megaphone} title={`Mes annonces (${annonces.length})`} testid="dashboard-annonces">
          {annonces.length === 0 ? (
            <p className="text-sm text-stone-500">Aucune annonce déposée.</p>
          ) : (
            <div className="space-y-3">
              {annonces.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-3 border border-[#E5DFD5] rounded-2xl px-4 py-3 text-sm">
                  {a.photos?.[0] && (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${a.photos[0].url}?viewer_id=${user.id}`} alt="" className="w-12 h-12 rounded-xl object-cover" />
                  )}
                  <span className="font-semibold text-stone-900">{CATEGORIES_ABONNEMENT[a.categorie]?.name}</span>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: CATEGORIES_ABONNEMENT[a.categorie]?.color }}>
                    {PALIERS[a.palier]}
                  </span>
                  <span className={`text-xs font-semibold ${a.statut === "active" ? "text-emerald-700" : "text-stone-400"}`}>
                    {a.statut === "active" ? `Active jusqu'au ${formatDate(a.date_expiration)}` : "Expirée"}
                  </span>
                  <span className="text-xs text-stone-500 ml-auto">{a.photos?.length || 0} photo(s){a.video ? " + vidéo" : ""}</span>
                </div>
              ))}
            </div>
          )}
          <Link to="/annonces" data-testid="dashboard-vers-annonces" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#E05638] hover:underline">
            Déposer une annonce <ArrowRight size={14} />
          </Link>
        </Section>

        <Section icon={Receipt} title="Historique des paiements" testid="dashboard-paiements">
          {historiquePaiements.length === 0 ? (
            <p className="text-sm text-stone-500">Aucun paiement pour l'instant.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-[#E5DFD5]">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Libellé</th>
                  <th className="py-2 pr-4">Montant</th>
                  <th className="py-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {historiquePaiements.map((h, i) => (
                  <tr key={i} className="border-b border-[#F0EBE3] text-stone-700">
                    <td className="py-2.5 pr-4 whitespace-nowrap">{formatDate(h.date)}</td>
                    <td className="py-2.5 pr-4 capitalize">{h.libelle}</td>
                    <td className="py-2.5 pr-4 font-semibold">{formatFCFA(h.montant)}</td>
                    <td className="py-2.5">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${h.statut === "accepte" ? "bg-emerald-100 text-emerald-800" : h.statut === "en_attente" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>
                        {h.statut === "accepte" ? "Accepté" : h.statut === "en_attente" ? "En attente" : "Refusé"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
      </main>
      <Footer />
    </div>
  );
}
